import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import { getEventUniqueNumber } from "src/utils/Utils.js";
import { clampOverscroll, preserveOffsetOnSlidesChanged, railArrowSx } from "src/utils/emblaUtils.js";
import { useDisplayImage } from "src/utils/useDisplayImage.js";
import { useDimensions } from "src/utils/useDimensions.js";
import { trackTileClicked } from "src/utils/sectionAnalytics.js";
import EventProfileBg from "src/components/blocks/event/EventProfileBg.js";
import DefaultImageBasketball from "src/components/blocks/event/DefaultImageBasketball.js";
import SubscriberStatusBadge from "src/components/blocks/SubscriberStatusBadge.js";
import SubscribeButton from "src/components/blocks/SubscribeButton.js";
import FreeBadge from "./FreeBadge.js";
import { eventArtworkSources, tileMetaLabel, toSubscribeCalendar } from "./calendarTileUtils.js";

const LEADERBOARD_OPTIONS = {
  align: "start",
  dragFree: true,
  containScroll: "trimSnaps",
  slidesToScroll: 3,
  watchSlides: preserveOffsetOnSlidesChanged,
};

/** Poster dimensions per breakpoint (2:3), scaling up on desktop like the rails. */
const POSTER = { compact: { w: 120, h: 180 }, wide: { w: 150, h: 225 } };
/** Rank numeral height as a fraction of the poster; the numeral font ≈ this × width. */
const NUMBER_SCALE = 0.96;
/** Gap fraction that keeps the widest numeral clear of the poster to its left. */
const GAP_SCALE = 0.58;
const SKELETON_ITEMS = Array.from({ length: 4 }, (_, index) => index + 1);

/** Outlined rank numeral tucked behind a poster, bottom-aligned. Caller adds `fontSize`. */
const RANK_NUMERAL_SX = {
  position: "absolute",
  zIndex: 0,
  bottom: 7,
  right: "85%",
  lineHeight: 0.7,
  fontWeight: 700,
  whiteSpace: "nowrap",
  pointerEvents: "none",
  paintOrder: "stroke fill",
  color: "#fff",
  WebkitTextStroke: "2.5px #111",
};

/** One loading tile that preserves a ranked leaderboard tile's geometry. */
const LeaderboardSkeletonTile = ({ rank, w, h, numberFont }) => (
  <Box sx={{ flex: "none", width: `${w}px` }}>
    <Box sx={{ position: "relative", height: `${h}px` }}>
      <Typography
        aria-hidden
        data-testid="leaderboard-skeleton-rank"
        sx={{ ...RANK_NUMERAL_SX, fontSize: `${numberFont}px` }}
      >
        {rank}
      </Typography>
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <Skeleton animation="wave" variant="rectangular" width="100%" height="100%" />
        <Skeleton
          animation="wave"
          variant="rounded"
          width={42}
          height={20}
          sx={{ position: "absolute", top: 6, right: 6, borderRadius: "10px" }}
        />
      </Box>
    </Box>
    <Skeleton animation="wave" variant="text" width="72%" sx={{ mt: 1, fontSize: "16px" }} />
    <Box sx={{ minHeight: "28px", display: "flex", alignItems: "center" }}>
      <Skeleton animation="wave" variant="rounded" width={64} height={20} sx={{ borderRadius: "10px" }} />
    </Box>
    <Skeleton animation="wave" variant="rounded" width="100%" height={36} sx={{ mt: 1, borderRadius: 1 }} />
  </Box>
);

/**
 * Loading counterpart to the ranked poster rail. The real rank numerals and
 * spacing stay visible because they are deterministic, while calendar-specific
 * artwork and metadata remain skeletons.
 */
export const LeaderboardRailSkeleton = () => {
  const { isMediumScreen } = useDimensions();
  const { w, h } = isMediumScreen ? POSTER.compact : POSTER.wide;
  const numberFont = Math.round(w * NUMBER_SCALE);
  const gap = Math.round(w * GAP_SCALE);

  return (
    <Box
      data-testid="league-leaderboard-skeleton"
      aria-hidden
      sx={{ display: "flex", gap: `${gap}px`, pl: `${gap}px`, overflow: "hidden", pb: 1 }}
    >
      {SKELETON_ITEMS.map((rank) => (
        <LeaderboardSkeletonTile key={rank} rank={rank} w={w} h={h} numberFont={numberFont} />
      ))}
    </Box>
  );
};

/**
 * The poster artwork, resolved through the same pipeline the recommended-card
 * tiles use, cropped to a 2:3 fill.
 *
 * @param {{ tileCalendar: object, height: number, homeTeamLogo: string|null,
 *   eventImageUrl: string|null, background: string|null }} props
 */
const PosterArt = ({
  tileCalendar,
  height,
  homeTeamLogo,
  eventImageUrl,
  background,
}) => {
  const { displayImage, loading, handleImageError } = useDisplayImage({
    type: "RecommendedCalendar",
    calendar: tileCalendar,
    homeTeamLogo,
    eventImageUrl,
    background,
    bakedTile: true,
    minuteTimeStamp: true,
  });

  return displayImage ? (
    <EventProfileBg
      loading={loading}
      profileImage={displayImage}
      calendarId={tileCalendar._id}
      eventSummary={tileCalendar.name}
      height={`${height}px`}
      width="100%"
      fill
      handleImageError={handleImageError}
    />
  ) : (
    <DefaultImageBasketball
      loading={loading}
      height={`${height}px`}
      width="100%"
      eventUniqueNumber={getEventUniqueNumber(String(tileCalendar._id), 0)}
    />
  );
};

/**
 * One ranked poster: the poster with a full-height outlined rank numeral
 * bottom-aligned and tucked behind it, the name and subscriber count below.
 *
 * @param {{ calendar: object, section: object, rank: number, w: number,
 *   h: number, numberFont: number }} props
 */
const LeaderboardTile = ({ calendar, section, rank, w, h, numberFont }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const tileCalendar = useMemo(
    () => toSubscribeCalendar(calendar),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calendar.calendarId, calendar.handle, calendar.name, calendar.picture, calendar.stanzaLink],
  );
  const artwork = useMemo(
    () => eventArtworkSources(calendar.mostRecentEvent, calendar.handle),
    [calendar.mostRecentEvent, calendar.handle],
  );
  const metaLabel = useMemo(
    () => tileMetaLabel(calendar),
    [calendar],
  );

  const open = () => {
    trackTileClicked({ user, section, calendar });
    navigate(
      getPageRoute("calendar", "CALENDAR_EVENTS", {
        handle: calendar.handle,
        calendarId: calendar.calendarId,
      }),
    );
  };

  return (
    <Box
      role="link"
      tabIndex={0}
      data-testid="leaderboard-tile"
      onClick={open}
      onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && open()}
      sx={{
        flex: "none",
        width: `${w}px`,
        cursor: "pointer",
        "&:focus-visible": { outline: (theme) => `2px solid ${theme.palette.primary.main}` },
      }}
    >
      <Box sx={{ position: "relative", height: `${h}px` }}>
        <Typography
          aria-hidden
          data-testid="leaderboard-rank"
          sx={{ ...RANK_NUMERAL_SX, fontSize: `${numberFont}px` }}
        >
          {rank}
        </Typography>
        <Box sx={{ position: "relative", zIndex: 1, width: "100%", height: "100%", borderRadius: "10px", overflow: "hidden" }}>
          <PosterArt tileCalendar={tileCalendar} height={h} {...artwork} />
          {!calendar.isStanzaPlus && <FreeBadge testId="leaderboard-free-badge" />}
        </Box>
      </Box>

      <Typography
        data-testid="leaderboard-name"
        title={calendar.name}
        sx={{
          mt: 1,
          fontSize: "16px",
          fontWeight: 600,
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          color: "text.primary",
        }}
      >
        {calendar.name}
      </Typography>
      {/* Fixed height so the pill, the event line, and empty all align, keeping
          every Add button on the same row across the rail. */}
      <Box
        data-testid="leaderboard-meta"
        sx={{ minHeight: "28px", display: "flex", alignItems: "center", overflow: "hidden" }}
      >
        <SubscriberStatusBadge sx={{ whiteSpace: "nowrap" }}>{metaLabel}</SubscriberStatusBadge>
      </Box>

      {/* The button owns its own dialogs, so keep its clicks off the tile. */}
      <Box
        sx={{ mt: 1 }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <SubscribeButton user={user} calendar={tileCalendar} handle={calendar.handle} isEventCard />
      </Box>
    </Box>
  );
};

/**
 * The "most popular" leaderboard as a Netflix Top-10 style poster rail: ranked
 * posters with full-height outlined numerals, scrolled horizontally. The gap is
 * sized so a numeral never overlaps the poster to its left.
 *
 * @param {{ calendars: Array<object>, section: object }} props
 */
const LeaderboardRail = ({ calendars, section }) => {
  const { isMediumScreen } = useDimensions();
  const { w, h } = isMediumScreen ? POSTER.compact : POSTER.wide;
  const numberFont = Math.round(w * NUMBER_SCALE);
  const gap = Math.round(w * GAP_SCALE);
  const plugins = useMemo(() => [WheelGesturesPlugin({ forceWheelAxis: "x" })], []);
  const [emblaRef, emblaApi] = useEmblaCarousel(LEADERBOARD_OPTIONS, plugins);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateArrowState = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;

    const onScroll = () => clampOverscroll(emblaApi);
    emblaApi.on("scroll", onScroll);
    emblaApi.on("select", updateArrowState);
    emblaApi.on("settle", updateArrowState);
    emblaApi.on("reInit", updateArrowState);
    updateArrowState();

    return () => {
      emblaApi.off("scroll", onScroll);
      emblaApi.off("select", updateArrowState);
      emblaApi.off("settle", updateArrowState);
      emblaApi.off("reInit", updateArrowState);
    };
  }, [emblaApi, updateArrowState]);

  if (!calendars.length) return null;

  return (
    <Box data-testid="see-all-leaderboard" sx={{ position: "relative" }}>
      {/* Dragging the rail would otherwise highlight the tile captions. */}
      <Box ref={emblaRef} sx={{ overflow: "hidden", userSelect: "none" }}>
        {/* Embla reInits on any child added here and restores the nearest
            snap, so nothing but slides may live in the container. */}
        <Box
          sx={{
            display: "flex",
            gap: `${gap}px`,
            pl: `${gap}px`,
            pb: 1,
          }}
        >
          {calendars.map((calendar, index) => (
            <LeaderboardTile
              key={calendar.calendarId}
              calendar={calendar}
              section={section}
              rank={index + 1}
              w={w}
              h={h}
              numberFont={numberFont}
            />
          ))}
        </Box>
      </Box>

      {!isMediumScreen && canScrollPrev && (
        <IconButton
          onClick={() => emblaApi?.scrollPrev()}
          aria-label="Scroll most popular left"
          sx={railArrowSx("left")}
        >
          <ChevronLeft />
        </IconButton>
      )}
      {!isMediumScreen && canScrollNext && (
        <IconButton
          onClick={() => emblaApi?.scrollNext()}
          aria-label="Scroll most popular right"
          data-testid="see-all-leaderboard-next"
          sx={railArrowSx("right")}
        >
          <ChevronRight />
        </IconButton>
      )}
    </Box>
  );
};

export default memo(LeaderboardRail);
